import {
  ChangeEvent,
  FocusEventHandler,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { CellProps, Col } from "components/Rable/types"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import GhostTextInput from "ui/inputs/GhostTextInput"
import { Popover } from "@material-ui/core"
import { Select } from "components/inputs/Select"
import { Universe } from "models/universes"
import get from "lodash/get"
import { useAuthenticated } from "providers/A12n"
import { useContextOptions } from "."
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"

function ReadOnlyCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { row, name } = props
  const initialValue = get(row, name, "")
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handlePopoverOpen = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        color="primary"
        className={cx("BodyBody-2BlackRegular", styles.wrap)}
      >
        {initialValue}
      </div>

      {open ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          className={styles.popover}
          classes={{ paper: styles.paper }}
        >
          <div className={styles.tooltip}>
            <span className={"BodyBody-2BlackRegular"}>{initialValue}</span>
          </div>
        </Popover>
      ) : null}
    </>
  )
}

function EditableCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { row, rowId, name, getValue, getOutput, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""

  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = parseNumber(evt.target.value)
    const hasValueDiff = initialValue.toString() !== value

    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
  }

  const handleChange = (value: string) => {
    setCurrentValue(value)
  }

  const numeric_rifm = useRifm({
    value: !!currentValue ? `${currentValue}` : "",
    accept: /[\d.]/g,
    onChange: (v) => handleChange(parseNumber(v)),
    format: (v) => (!!v ? formatFloatingPointNumber()(v) : ""),
  })

  const _value = numeric_rifm.value

  const _onChange = numeric_rifm.onChange

  const placeholder = t(`market.universes.add.new.${name as string}`)

  return (
    <GhostTextInput
      // error={!!error}
      // errorMessage={error}
      disabled={!hasAuthority(["AUTH_EDIT_UNIVERSE"])}
      value={_value}
      placeholder={placeholder}
      onChange={_onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
    />
  )
}

function SelectCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { row, rowId, name, getValue, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""
  const {
    genderOptions,
    ageRangeOptions,
    socioeconomicsOptions,
    typeOfWorkOptions,
  } = useContextOptions()
  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const handleChange = (
    evt: ChangeEvent<{
      name?: string | undefined
      value: unknown
    }>,
  ) => {
    const { value } = evt.target
    const hasValueDiff = currentValue.toString() !== value
    if (value) {
      const output = { [name]: value } as Partial<T>
      hasValueDiff && onUpdate(rowId, output)
      setCurrentValue(value)
    }
  }

  let cellOptions = [] as any[]

  switch (name) {
    case "variables.AGE_RANGE":
      cellOptions = ageRangeOptions || []
      break
    case "variables.SOCIOECONOMICS":
      cellOptions = socioeconomicsOptions || []
      break
    case "variables.GENDER":
      cellOptions = genderOptions || []
      break
    case "variables.TYPE_OF_WORK":
      cellOptions = typeOfWorkOptions || []
      break
    default:
      cellOptions = []
      break
  }

  const placeholder = t(`market.universes.add.new.${name as string}`)

  const disabled = !hasAuthority(["AUTH_EDIT_UNIVERSE"])

  return !disabled ? (
    <div className={styles.buttonContainer}>
      <Select
        name={name as string}
        options={cellOptions}
        value={currentValue}
        margin="none"
        size="normal"
        className={cx(styles.ghostSelect, {
          [styles.error]: !!error,
        })}
        placeholder={placeholder}
        onChange={handleChange}
        error={!!error}
      />
    </div>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {t(currentValue as string)}
    </div>
  )
}

const styles = {
  input: css`
    font-size: 13px;
    width: 100%;
    padding-bottom: var(--spacing-xs);
    padding-right: 0px;
    border-style: dashed;
    margin-bottom: 1px;
    height: 33px;
    > input {
      text-align: end;
      ::placeholder {
        color: var(--dark-grey);
      }
    }
    :hover {
      cursor: pointer;
    }
  `,
  ghostInput: css`
    :focus-within {
      border: 2px dashed var(--primary-main, teal);
    }
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
  ghostSelect: css`
    align-items: center;
    border: none;
    width: 100%;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      width: 100%;
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiInputBase-root {
      font-size: 13px;
      width: 100%;
    }
    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
      text-align: start;
    }
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
  wrap: css`
    width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  paper: css`
    pointer-events: auto;
  `,
  popover: css`
    margin-top: var(--spacing-xxs);
    pointer-events: none;
  `,
  tooltip: css`
    padding: var(--spacing-xs);
    border-radius: 4px;
    box-shadow: 0 0 18px 0 var(--light-grey);
  `,
}

export const COLUMNS: Col<Universe>[] = [
  {
    id: "AGE_RANGE",
    name: "variables.AGE_RANGE",
    cell: ReadOnlyCell,
    newRowCell: SelectCell,
    existsInCreate: true,
    required: true,
  },
  {
    id: "SOCIOECONOMICS",
    name: "variables.SOCIOECONOMICS",
    cell: ReadOnlyCell,
    newRowCell: SelectCell,
    existsInCreate: true,
    required: false,
  },
  {
    id: "GENDER",
    name: "variables.GENDER",
    cell: ReadOnlyCell,
    newRowCell: SelectCell,
    existsInCreate: true,
  },
  {
    id: "TYPE_OF_WORK",
    name: "variables.TYPE_OF_WORK",
    cell: ReadOnlyCell,
    newRowCell: SelectCell,
    existsInCreate: true,
  },
  {
    id: "population",
    name: "population",
    cell: EditableCell,
    existsInCreate: true,
  },
]
